// @ts-check
import { computed, ref } from 'vue';
import { get, has } from 'lodash';
import { useReactiveSet } from 'vue-reactive-collection';
import { ZodError } from 'zod';

import i18n from '@/locales';

/**
 * Translates the error message if it is a translation key.
 * Otherwise, returns the original error message.
 *
 * @param {string} message - The error to translate.
 * @returns {string} - The translated error.
 */
function maybeTranslateError(message) {
  if (i18n.te(message)) {
    return i18n.t(message);
  }

  return message;
}

/**
 * Custom hook for performing validation on form fields using a zod schema.
 *
 * @template {import('zod').ZodTypeAny} TSchema
 * @template {TSchema['_input']} TInput
 *
 * @param {import('vue').Ref<TInput>} updateData - The reactive reference to the form update object.
 * @param {TSchema} schema - The zod schema object used for validation
 * @param {import('vue').Ref<boolean>} [otherInvalid]
 */
export function useValidation(updateData, schema, otherInvalid) {
  /** @typedef {import('zod').infer<typeof schema>} TOutput */
  /** @typedef {import('zod').SafeParseReturnType<TInput,TOutput>} TParsed */
  /** @typedef {import('zod').ZodError<TInput>} TErrors */

  /** @type {import('vue').Ref<TParsed|undefined>} */
  const result = ref();
  /** @type {import('vue').Ref<TErrors|null>} */
  const rawErrors = ref(null);
  /** @type {import('vue').Ref<Set<string|TSchema>>} */
  const fieldsToValidate = useReactiveSet();

  /**
   * Did the validation pass?
   * Returns true if not validated yet or the fields to validate don't have errors.
   */
  const isValid = computed(() => {
    if (!result.value) {
      return true;
    }

    if (otherInvalid?.value && fieldsToValidate.value.has(schema)) {
      return false;
    }

    if (result.value.success === true) {
      return true;
    }

    if (fieldsToValidate.value.has(schema)) {
      return false;
    }

    return !result.value.error.errors.some(error =>
      fieldsToValidate.value.has(error.path.join('.')));
  });

  /**
   * The data object if the validation was successful.
   * @type {import('vue').Ref<TOutput|null>}
   */
  const data = computed(() => {
    if (!isValid.value || !result.value) {
      return null;
    }

    return result.value.success === true ? result.value.data : null;
  });

  /**
   * The formatted errors object if the validation was unsuccessful.
   */
  const errors = computed(() => {
    if (!rawErrors.value) {
      return undefined;
    }

    return rawErrors.value.format();
  });

  /**
   * Get the errors object for the fields to validate.
   * Returns all errors if getting for 'schema' otherwise filters the errors for fields to validate.
   * @return {TErrors|null}
   */
  function getErrors() {
    if (!result.value || result.value.success === true) {
      return null;
    }

    if (!fieldsToValidate.value.size || fieldsToValidate.value.has(schema)) {
      return result.value.error;
    }

    return new ZodError(result.value.error.errors.filter(error =>
      fieldsToValidate.value.has(error.path.join('.'))));
  }

  /**
   * Validates against the schema.
   *
   * @param {string} [field] - Optional field to validate, if not provided, validates all fields.
   */
  function validate(field) {
    // Always revalidate errors
    if (rawErrors.value) {
      fieldsToValidate.value.clear();
      rawErrors.value.errors.forEach((issue) => {
        fieldsToValidate.value.add(issue.path.join('.'));
      });
    }

    // If a field is provided, validate that field
    if (!field) {
      fieldsToValidate.value.add(schema);
    } else {
      fieldsToValidate.value.delete(schema);
      fieldsToValidate.value.add(field);
    }

    result.value = schema.safeParse(updateData.value);

    rawErrors.value = getErrors();
  }

  return {
    isValid,
    data,
    errors,
    validate,
  };
}

/**
 * @template TVariables
 * @param {import('@tanstack/vue-query').MutateFunction<any,any,TVariables,any>} callback
 * @param {import('vue').Ref<TVariables|null>} data
 * @param {() => void} validate
 */
export function useUpdateWithValidation(callback, data, validate) {
  return function update() {
    validate();

    if (data.value) {
      return callback(data.value);
    }

    return Promise.reject();
  };
}

/**
 * Access errors from validation.
 * @param {import('vue').Ref<import('zod').ZodFormattedError<any>|undefined>} errors - The errors object.
 */
export function useErrors(errors) {
  /**
   * Adds '_errors' to the given path if it's an array, otherwise appends '._errors' to the path.
   * @param {string|string[]} path - The path to add _errors to.
   * @returns {string|string[]} - The modified path with _errors.
   */
  function postfixPath(path) {
    return Array.isArray(path) ? [...path, '_errors'] : `${path}._errors`;
  }

  /**
   * Checks if the given paths have errors.
   * @param {...(string|string[])} paths - The paths to check for errors.
   * @returns {boolean} - True if the any of the paths have errors, false otherwise.
   */
  function hasErrors(...paths) {
    return paths.some(path => has(errors.value, postfixPath(path)));
  }

  /**
   * Retrieves the first error for the given path.
   * @param {string|string[]} path - The path to retrieve errors for.
   * @returns {string|null} - The first error for the given path, or null if no errors are found.
   */
  function getError(path) {
    const errorsArray = get(errors.value, postfixPath(path));
    return errorsArray ? maybeTranslateError(errorsArray[0]) : null;
  }

  /**
   * Retrieves the errors for the given path.
   * @param {string|string[]} path - The path to retrieve errors for.
   * @returns {string[]} - The array of errors for the given path.
   */
  function getErrors(path) {
    const errorsArray = get(errors.value, postfixPath(path));
    return errorsArray ? errorsArray.map(maybeTranslateError) : [];
  }

  return {
    hasErrors,
    getErrors,
    getError,
  };
}
